// =====================================================================
// MOHIT STUDIO — SITE CONTENT (single source of truth)
// ---------------------------------------------------------------------
// Everything you may want to change lives in this one file:
// photos, films, prices, services, testimonials, phone numbers.
// Edit the text below and the website updates automatically.
// =====================================================================

// Premium stock placeholders — swap URLs with your own photos anytime.
const U = (id, w = 900) =>
  `https://images.unsplash.com/${id}?q=80&w=${w}&auto=format&fit=crop`;
const P = (id, w = 900) =>
  `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&w=${w}`;

export const STUDIO = {
  name: "Mohit Studio",
  eyebrow: "Wedding Photography & Cinematography · Keshod, Gujarat",
  tagline: "Every Moment. Forever Framed.",
  taglineHi: "Yaadein, hamesha ke liye.",
  city: "Keshod, Gujarat",
  serviceAreas: "Keshod · Junagadh · Rajkot · Somnath · Diu · Destination Weddings",
  instagram: {
    handle: "@mohit_studiokeshod",
    url: "https://www.instagram.com/mohit_studiokeshod",
  },
  whatsapp:
    "https://wa.me/919106826628?text=Hi%20Mohit%20Studio%2C%20I%20want%20to%20enquire%20about%20wedding%20photography.",
  contacts: [
    {
      name: "Mohit Gehani",
      role: "Photographer & Filmmaker",
      phone: "+91 91068 26628",
      tel: "tel:+919106826628",
    },
    {
      name: "Vinod Bhai Gehani",
      role: "The Gehani Legacy",
      phone: "+91 98245 90503",
      tel: "tel:+919824590503",
    },
  ],
};

// ------------------------- HERO SLIDESHOW ----------------------------
export const HERO_SLIDES = [
  {
    src: P(27443997, 1920),
    alt: "Bride and groom dancing at a candlelit wedding in Keshod",
  },
  {
    src: U("photo-1599462616558-2b75fd26a283", 1920),
    alt: "Groom kissing the bride's forehead in royal Gujarati wedding attire",
  },
  {
    src: P(35069916, 1920),
    alt: "Couple embracing during their outdoor wedding ceremony",
  },
  {
    src: P(35327940, 1920),
    alt: "Joyful bride at her colourful haldi ceremony",
  },
  {
    src: P(32679917, 1920),
    alt: "Vibrant royal Indian wedding rituals",
  },
];

// ------------------------- EDITORIAL MARQUEE -------------------------
export const MARQUEE_ITEMS = [
  "Weddings",
  "Cinematic Films",
  "Pre-Wedding",
  "Engagement · Sagai",
  "Haldi & Mehendi",
  "Drone Coverage",
  "Premium Albums",
  "Reels & Teasers",
];

// ------------------------------ ABOUT --------------------------------
export const ABOUT = {
  eyebrow: "Our Story · Keshod, Gujarat",
  title: "Born in Keshod. Framing love, frame by frame.",
  lead: "Mohit Studio is the Gehani family's love letter to weddings.",
  paragraphs: [
    "What began with Vinod Bhai Gehani's camera and an unerring eye for real emotion is today one of Saurashtra's most trusted wedding studios — carried forward by his son, Mohit Gehani. Two generations, one belief: the best photographs happen when you forget the camera is there.",
    "From the haldi-morning giggles to the final vidaai tear, we stay quiet, blend in, and catch what matters — the stolen glance, the tight hug, the grandfather's blessing. No stiff poses, no rushed shots. Just your people, being yours.",
  ],
  quote: "Yaadein, hamesha ke liye.",
  quoteMeaning: "Memories, forever framed.",
  chips: [
    "Family-run, film-roll roots",
    "Keshod · Junagadh · Rajkot · Somnath · Diu",
    "Destination-ready crew",
  ],
  images: {
    tall: {
      src: P(17169150, 1000),
      alt: "Mohit capturing a couple's intimate moment through the camera",
    },
    wide: {
      src: U("photo-1611550287705-7ff8b459c8eb", 1000),
      alt: "Photographer at work with a professional camera at a wedding",
    },
  },
};

// ---------------------------- SERVICES -------------------------------
// Icon names map to Lucide icons in Services.jsx — keep them valid.
export const SERVICES = [
  {
    icon: "Camera",
    title: "Wedding Photography",
    desc: "Unposed, unscripted, unforgettable. We chase the glances, the laughter and the happy tears that make your day yours.",
  },
  {
    icon: "Clapperboard",
    title: "Cinematic Wedding Films",
    desc: "Feature-style films with music, motion and meaning — your love story, on the big screen.",
  },
  {
    icon: "HeartHandshake",
    title: "Pre-Wedding Shoots",
    desc: "Golden-hour romance across Keshod's coasts, palaces and the wild Gir backroads.",
  },
  {
    icon: "Gem",
    title: "Engagement / Sagai",
    desc: "The first promise, framed forever — intimate, unhurried coverage of rings and rituals.",
  },
  {
    icon: "Flower2",
    title: "Haldi & Mehendi",
    desc: "Sunshine ceremonies and intricate henna — the colour-soaked days before the vows.",
  },
  {
    icon: "Radar",
    title: "Drone Coverage",
    desc: "Sweeping aerials that crown your venue in cinematic grandeur — baraat to beach mandap.",
  },
  {
    icon: "BookOpen",
    title: "Premium Albums",
    desc: "Handcrafted, lay-flat heirlooms, designed page by page and printed to last generations.",
  },
  {
    icon: "Smartphone",
    title: "Reels & Teasers",
    desc: "Instagram-ready edits delivered while your wedding is still the talk of the town.",
  },
];

// ---------------------------- PORTFOLIO ------------------------------
// category must be one of: "Wedding" | "Pre-Wedding" | "Haldi/Mehendi" | "Engagement"
// orientation: "tall" | "wide" | "square" (controls the masonry rhythm)
export const PORTFOLIO = [
  { id: "w1", category: "Wedding", title: "Mandap Promises — Junagadh", orientation: "wide", src: U("photo-1587271636175-90d58cdad458") },
  { id: "h1", category: "Haldi/Mehendi", title: "Mehendi Letters", orientation: "square", src: U("photo-1730003727902-3643640a3d43") },
  { id: "p1", category: "Pre-Wedding", title: "Riverbank Romance — Diu", orientation: "tall", src: U("photo-1630526720753-aa4e71acf67d") },
  { id: "w2", category: "Wedding", title: "The Royal Glance", orientation: "tall", src: U("photo-1665960213508-48f07086d49c") },
  { id: "e1", category: "Engagement", title: "Ring Exchange — Rajkot", orientation: "wide", src: P(6413165) },
  { id: "h2", category: "Haldi/Mehendi", title: "Pehli Haldi", orientation: "tall", src: P(27960939) },
  { id: "p2", category: "Pre-Wedding", title: "Into the Woods — Gir", orientation: "tall", src: U("photo-1735052712464-9d24b69be5f5") },
  { id: "w3", category: "Wedding", title: "The Laugh We Live For", orientation: "wide", src: P(19609201) },
  { id: "e2", category: "Engagement", title: "Hands Forever — Sagai", orientation: "square", src: P(38274758) },
  { id: "h3", category: "Haldi/Mehendi", title: "Haldi Gold", orientation: "tall", src: U("photo-1781154110379-f2447cd4e2ca") },
  { id: "e3", category: "Engagement", title: "Two Rings, One Promise", orientation: "wide", src: U("photo-1542042161784-26ab9e041e89") },
  { id: "w4", category: "Wedding", title: "Veil & Sherwani — Keshod", orientation: "tall", src: P(38147801) },
];

export const PORTFOLIO_FILTERS = [
  "All",
  "Wedding",
  "Pre-Wedding",
  "Haldi/Mehendi",
  "Engagement",
];

// ------------------------------ FILMS --------------------------------
// url: replace "#" with your real YouTube video / Instagram Reel link.
export const FILMS = [
  {
    title: "A Night Under the Lights",
    meta: "Wedding Film · Somnath",
    duration: "2:45",
    url: "#",
    poster: P(1735052709927, 1200),
  },
  {
    title: "Rang Aur Rasm",
    meta: "Haldi Reel · Junagadh",
    duration: "0:58",
    url: "#",
    poster: P(17262009, 1200),
  },
  {
    title: "Saat Samundar Paar",
    meta: "Destination Wedding · Diu",
    duration: "3:12",
    url: "#",
    poster: U("photo-1610173827002-62c0f1f05d04", 1200),
  },
  {
    title: "The First Dance",
    meta: "Wedding Teaser · Rajkot",
    duration: "1:20",
    url: "#",
    poster: U("photo-1585607344893-43a4bd91169a", 1200),
  },
];

// ---------------------------- PACKAGES -------------------------------
// price: all packages show "Contact for quote" — edit feature lists freely.
export const PACKAGES = [
  {
    id: "classic",
    name: "Classic",
    subtitle: "The Essentials",
    featured: false,
    features: [
      "Full-day candid photography",
      "Traditional coverage of all rituals",
      "300+ hand-edited photos",
      "Beautifully designed photo book",
      "One-photographer team",
    ],
  },
  {
    id: "premium",
    name: "Premium",
    subtitle: "The Full Story",
    featured: true,
    features: [
      "Everything in Classic",
      "Cinematic wedding film (3–5 min)",
      "Pre-wedding shoot included",
      "Drone aerial coverage",
      "Express teaser within 48 hours",
      "Two-photographer team",
    ],
  },
  {
    id: "royal",
    name: "Royal",
    subtitle: "The Celebration",
    featured: false,
    features: [
      "Everything in Premium",
      "Multi-event coverage — Haldi, Mehendi, Sagai",
      "Same-day edit premiere at the reception",
      "Framed gallery pieces for your home",
      "Luxury lay-flat album set",
      "Full crew — photographers, cinematographers & drone",
    ],
  },
];

// --------------------------- WHY CHOOSE US ---------------------------
export const WHY_US = [
  {
    icon: "Sparkles",
    title: "Candid Storytelling",
    desc: "No stiff poses. We document the stolen glances and happy tears exactly as they happen.",
  },
  {
    icon: "Aperture",
    title: "Latest Equipment",
    desc: "Full-frame cinema cameras, pro lenses and drones — your memories shot in stunning 4K.",
  },
  {
    icon: "Clock",
    title: "On-Time Delivery",
    desc: "Teasers in 48 hours, complete films in weeks. Your excitement never has to wait.",
  },
  {
    icon: "Users",
    title: "Personal Attention",
    desc: "A family-run studio where your wedding gets the care we'd give our own.",
  },
];

// --------------------------- TESTIMONIALS ----------------------------
// PLACEHOLDER words — replace with real client testimonials as they come.
export const TESTIMONIALS = [
  {
    quote:
      "They didn't just photograph our wedding — they caught moments we didn't even know happened. Watching our film still makes us cry happy tears.",
    couple: "Aakash & Priya",
    venue: "Junagadh",
  },
  {
    quote:
      "From our sagai to the vidaai, Mohit bhai treated us like family. Every single photo feels like a scene from a movie.",
    couple: "Rahul & Meera",
    venue: "Rajkot",
  },
  {
    quote:
      "Our haldi was a riot of colour and laughter — and the reels had the whole family dancing. Best decision we made for our wedding.",
    couple: "Jay & Himani",
    venue: "Keshod",
  },
  {
    quote:
      "They flew the drone over our beach wedding in Diu and the footage was pure magic. Professional, warm and always on time.",
    couple: "Smit & Drashti",
    venue: "Diu",
  },
];

// -------------------------- INSTAGRAM STRIP --------------------------
export const INSTAGRAM_IMAGES = [
  P(27443997, 600),
  U("photo-1599462616558-2b75fd26a283", 600),
  P(35327940, 600),
  U("photo-1630526720753-aa4e71acf67d", 600),
  P(19609201, 600),
  U("photo-1781154110379-f2447cd4e2ca", 600),
  P(6413165, 600),
  U("photo-1587271636175-90d58cdad458", 600),
];

// ----------------------------- BOOKING -------------------------------
export const EVENT_TYPES = [
  "Wedding",
  "Pre-Wedding",
  "Engagement / Sagai",
  "Haldi & Mehendi",
  "Reels & Teasers",
  "Other",
];

// ------------------------------ FOOTER -------------------------------
export const NAV_LINKS = [
  { label: "About", id: "about" },
  { label: "Services", id: "services" },
  { label: "Portfolio", id: "portfolio" },
  { label: "Films", id: "films" },
  { label: "Packages", id: "packages" },
  { label: "Contact", id: "book" },
];
